// Selector de PROYECTO en la cabecera. Sustituye al antiguo ProjectTitle estático
// (que solo mostraba el nombre y cuyo "cambiar" llevaba al Dashboard, no a los
// proyectos). Ahora es un desplegable: proyecto actual + tus proyectos recientes +
// "Subir nuevo guion" + "Ver todos". Así cambiar/cargar proyecto es un clic desde
// cualquier pantalla.
import { useState, useRef, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FolderOpen, ChevronDown, FileUp, LayoutGrid, Check } from 'lucide-react';
import { useAnalysisJob, useAnalysisJobs } from '@/hooks/useScriptAnalysis';
import { cleanProjectTitle, recentProjects } from '@/lib/projects';

export default function ProjectSwitcher() {
  const [sp] = useSearchParams();
  const jobId = sp.get('job');
  const navigate = useNavigate();
  const { data: job } = useAnalysisJob(jobId);
  const { data: jobs } = useAnalysisJobs();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const projects = recentProjects(jobs as Parameters<typeof recentProjects>[0]);
  const currentTitle = jobId ? (cleanProjectTitle(job?.file_name) || 'Cargando…') : 'Elige un proyecto';

  function go(path: string) {
    setOpen(false);
    navigate(path);
  }

  return (
    <div ref={ref} className="relative min-w-0">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 min-w-0 max-w-[340px] rounded-md px-2 py-1 hover:bg-pelip-bg-elev-2 transition-colors"
        title="Cambiar de proyecto"
      >
        <FolderOpen size={14} className="text-pelip-text-faint shrink-0" />
        <span className="text-[10px] uppercase tracking-wider text-pelip-text-faint font-mono shrink-0 hidden sm:inline">Proyecto</span>
        <span className={`text-sm font-semibold truncate ${jobId ? 'text-pelip-text-soft' : 'text-pelip-text-faint'}`} title={job?.file_name ?? undefined}>
          {currentTitle}
        </span>
        <ChevronDown size={14} className="text-pelip-text-faint shrink-0" />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 w-72 bg-pelip-bg-elev border border-pelip-border-strong rounded-md shadow-xl overflow-hidden z-50">
          <div className="max-h-[320px] overflow-y-auto py-1">
            {projects.length === 0 ? (
              <div className="px-3 py-3 text-xs text-pelip-text-faint">Aún no tienes proyectos.</div>
            ) : (
              projects.map((p) => {
                const active = p.jobId === jobId;
                return (
                  <button
                    key={p.key}
                    onClick={() => go(`/?job=${p.jobId}`)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-pelip-bg-elev-2 ${active ? 'bg-pelip-bg-elev-2' : ''}`}
                  >
                    {active ? <Check size={13} className="text-pelip-accent shrink-0" /> : <span className="w-[13px] shrink-0" />}
                    <span className="truncate text-pelip-text-soft">{p.name}</span>
                    {p.isDemo && <span className="ml-auto text-[9px] uppercase tracking-wider text-pelip-text-faint border border-pelip-border rounded px-1 py-0.5 shrink-0">demo</span>}
                  </button>
                );
              })
            )}
          </div>

          <div className="border-t border-pelip-border">
            <button
              onClick={() => go('/subir')}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-pelip-accent hover:bg-pelip-bg-elev-2"
            >
              <FileUp size={13} className="shrink-0" />
              Subir nuevo guion
            </button>
            <button
              onClick={() => go('/proyectos')}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-pelip-text-soft hover:bg-pelip-bg-elev-2"
            >
              <LayoutGrid size={13} className="shrink-0" />
              Ver todos los proyectos
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
